import React, { useEffect, useState } from 'react';
import { FaWhatsapp } from 'react-icons/fa';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { BADGES, formatINR } from '../../../data/games';

const FeaturedGamesCarousel = ({ featuredGames = [], onWhatsAppClick, onPreviewClick }) => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || featuredGames?.length < 2) return undefined;
    const t = setInterval(() => setIndex((i) => (i + 1) % featuredGames.length), 6000);
    return () => clearInterval(t);
  }, [paused, featuredGames?.length]);

  if (!featuredGames?.length) return null;

  const game = featuredGames[Math.min(index, featuredGames.length - 1)];
  const badge = BADGES[game?.badge] ?? BADGES.classic;

  return (
    <div
      className="panel-gold relative overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="grid gap-0 lg:grid-cols-[1.1fr_1fr]">
        {/* Art */}
        <div className="relative min-h-[16rem] overflow-hidden lg:min-h-[24rem]">
          <Image key={game?.id} src={game?.image} alt={game?.name} className="animate-fade-in h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0C0C16] via-transparent to-transparent lg:bg-gradient-to-r lg:from-transparent lg:via-transparent lg:to-[#0C0C16]" />

          <span className={`absolute left-5 top-5 flex items-center gap-1.5 rounded-full bg-gradient-to-r ${badge.className} px-3 py-1 text-[0.62rem] font-bold text-[#150F02]`}>
            <Icon name={badge.icon} size={11} color="#150F02" /> {badge.label}
          </span>

          <span className="absolute bottom-5 left-5 flex items-center gap-2 rounded-full border border-white/15 bg-black/60 px-3 py-1.5 text-xs text-white/80 backdrop-blur">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-victory-red" />
            {game?.playerCount?.toLocaleString('en-IN')} players seated
          </span>
        </div>

        {/* Copy */}
        <div className="relative flex flex-col justify-center p-7 sm:p-10">
          <span className="badge-pill self-start"><Icon name="Sparkles" size={12} /> Featured table</span>

          <h3 key={`${game?.id}-t`} className="animate-rise mt-5 font-heading text-3xl font-bold text-white sm:text-4xl">
            {game?.name}
          </h3>
          <p className="mt-2 font-display text-sm text-royal-gold">{game?.tagline}</p>
          <p className="mt-4 text-sm leading-relaxed text-white/60">{game?.description}</p>

          <div className="mt-6 grid grid-cols-3 gap-3">
            {[
              { l: 'Min bet', v: formatINR(game?.minBet) },
              { l: 'Max win', v: formatINR(game?.maxWin) },
              { l: 'RTP', v: game?.rtp },
            ].map((s) => (
              <div key={s.l} className="rounded-xl border border-white/8 bg-white/[0.05] px-3 py-2.5 text-center">
                <p className="text-[0.58rem] uppercase tracking-[0.14em] text-white/40">{s.l}</p>
                <p className="mt-0.5 font-display text-sm font-semibold text-white">{s.v}</p>
              </div>
            ))}
          </div>

          <div className="mt-7 flex flex-wrap gap-3">
            <button onClick={() => onWhatsAppClick(game)} className="btn-3d btn-3d-wa !py-3 text-sm">
              <FaWhatsapp size={17} /> Book my seat
            </button>
            <button onClick={() => onPreviewClick(game)} className="btn-3d btn-3d-ghost !py-3 text-sm">
              <Icon name="Eye" size={16} /> Rules &amp; strategy
            </button>
          </div>

          <div className="mt-8 flex items-center gap-2">
            {featuredGames.map((g, i) => (
              <button
                key={g.id}
                onClick={() => setIndex(i)}
                aria-label={`Show ${g.name}`}
                className={`h-1.5 rounded-full transition-all duration-500 ${
                  i === index ? 'w-9 bg-gradient-royal' : 'w-3 bg-white/20 hover:bg-white/40'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeaturedGamesCarousel;
